import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { Checkbox, CheckboxProps } from '@progress/kendo-react-inputs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';

interface CheckboxStateWrapperProps<T extends FieldValues>
  extends UseControllerProps<T>, Omit<CheckboxProps, 'defaultValue' | 'name'> {
  label?: string;
  disabled?: boolean;
  hint?: string;
  type?: string;
  placeholder?: string;
  inverted?: boolean;
  wrapperClassName?: string;
}

export const CheckboxStateWrapper = <T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  disabled,
  hint,
  inverted,
  wrapperClassName,
  label,
  ...rest
}: CheckboxStateWrapperProps<T>) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
  });

  const id = field.name;
  const showValidationMessage = Boolean(isTouched && error?.message);
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';

  const { onChange, value, ...restField } = field;
  return (
    <FieldWrapper className={wrapperClassName}>
      <div className={'k-form-field-wrap'}>
        <Checkbox
          {...rest}
          {...restField}
          id={id}
          valid={!showValidationMessage}
          validationMessage={error?.message}
          required={Boolean(rules?.required)}
          ariaDescribedBy={`${hintId} ${errorId}`}
          disabled={disabled}
          onChange={(e) => {
            if (inverted) {
              onChange(!e.value);
            } else {
              onChange(e.value);
            }
          }}
          value={inverted ? !value : value}
        >
          <Label editorId={id} className={'k-checkbox-label text-center'}>
            <span
              className="[&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800 [&_b]:font-bold [&_em]:italic [&_i]:italic [&_strong]:font-bold"
              // Kendo's Label onClick preventDefaults every click and forwards
              // it to the checkbox; links in the label must navigate instead.
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) {
                  e.stopPropagation();
                }
              }}
              dangerouslySetInnerHTML={{ __html: label || '' }}
            ></span>
          </Label>
        </Checkbox>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{error?.message}</Error>}
      </div>
    </FieldWrapper>
  );
};
